import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logoutUser, clear2FAState } from '../../store/slices/authSlice';
import FloatingSidebar from '../FloatingSidebar/FloatingSidebar';
import toast from 'react-hot-toast';

const Header = ({ onLoginClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileClicked, setIsProfileClicked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get authentication state from Redux
  const { isAuthenticated, user, loading } = useAppSelector((state) => state.auth);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSidebar = () => {
    // console.log('toggleSidebar called - current state:', isSidebarOpen);
    setIsSidebarOpen(!isSidebarOpen);
    // console.log('toggleSidebar - new state will be:', !isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleLoginClick = () => {
    // Clear 2FA session data when login button is clicked
    sessionStorage.removeItem('twofa_email');
    sessionStorage.removeItem('twofa_password');
    sessionStorage.removeItem('twofa_questions');
    sessionStorage.removeItem('twofa_phone');
    
    // Clear 2FA state from Redux
    dispatch(clear2FAState());
    navigate('/');
    
    // Call the parent's login handler
    onLoginClick();
  };

  return (
    <header className="bg-white fixed top-0 left-0 right-0 z-[1000] shadow-sm border-b border-jet-border">
      <div className="w-[86%] mx-auto md:px-0">
        <div className="flex justify-between items-center h-[60px] md:h-[70px]">
          <div className="flex-shrink-0">
            <Link to="/">
              <img src="/jetscanner-logo.svg" alt="JetScanner360" className="h-7" />
            </Link>
          </div>

          <div className="flex items-center gap-0">
            <nav className={`${isMobileMenuOpen ? 'flex' : 'hidden'} md:flex absolute md:relative top-full md:top-auto left-0 md:left-auto right-0 md:right-auto bg-white md:bg-transparent shadow-lg md:shadow-none transform ${isMobileMenuOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible'} md:transform-none md:opacity-100 md:visible transition-all duration-300 ease-in-out`}>
              <ul className="flex flex-col md:flex-row list-none gap-4 md:gap-2 items-center m-0 p-4 md:p-0">
                <li><Link to="/search-flight" className={`text-[16px] font-semibold block py-[9px] px-[10px] mx-[7px] no-underline relative transition-all duration-200 ${isActive('/search-flight') ? 'text-[#5392f9]' : 'text-[#737373] hover:text-[#5392f9]'}`}>Search Flight</Link></li>
                <li><Link to="/who-we-are" className={`text-[16px] font-semibold block py-[9px] px-[10px] mx-[7px] no-underline relative transition-all duration-200 ${isActive('/who-we-are') ? 'text-[#5392f9]' : 'text-[#737373] hover:text-[#5392f9]'}`}>Who We Are</Link></li>
                <li><Link to="/what-we-do" className={`text-[16px] font-semibold block py-[9px] px-[10px] mx-[7px] no-underline relative transition-all duration-200 ${isActive('/what-we-do') ? 'text-[#5392f9]' : 'text-[#737373] hover:text-[#5392f9]'}`}>What We Do</Link></li>
                <li><Link to="/how-it-works" className={`text-[16px] font-semibold block py-[9px] px-[10px] mx-[7px] no-underline relative transition-all duration-200 ${isActive('/how-it-works') ? 'text-[#5392f9]' : 'text-[#737373] hover:text-[#5392f9]'}`}>How It Works</Link></li>

                {isAuthenticated && user && !location.pathname.toLowerCase().includes('email-verify') ? (
                  <>
                    <li 
                      onClick={() => setIsProfileClicked(!isProfileClicked)}
                      className={`flex items-center space-x-2 py-[9px] px-[10px] mx-[7px] rounded-xl cursor-pointer transition-all duration-200 border-4 ${
                        isProfileClicked ? 'border-[#ffd200] bg-[#fffef0]' : 'border-transparent'
                      }`}
                    >
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                        {user.image_url || user.image ? (
                          <img
                            src={user.image_url || user.image}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23999"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';
                            }}
                          />
                        ) : (
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-[16px] font-semibold ${isProfileClicked ? 'text-[#5392f9]' : 'text-[#737373]'}`}>
                        {user.first_name?.toUpperCase()} {user.last_name?.toUpperCase()}
                      </span>
                    </li>
                  </>
                ) : (
                  <li><button onClick={handleLoginClick} className="text-[16px] font-semibold block block py-[9px] px-[10px] mx-[7px] rounded-[5px] no-underline relative transition-colors duration-200 text-[#737373] hover:text-[#5392f9] bg-transparent border-none cursor-pointer">Login/Sign-up</button></li>
                )}
              </ul>
            </nav>

            {/* Desktop Sidebar Toggle - Only show when authenticated and not on verification page */}
            {isAuthenticated && user && !location.pathname.toLowerCase().includes('email-verify') && (
              <button
                id="sidebar-toggle"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // console.log('Hamburger menu clicked! Current state:', isSidebarOpen);
                  toggleSidebar();
                }}
                className={`hidden md:flex items-center justify-center transition-all duration-200 ml-4 cursor-pointer bg-transparent rounded-xl ${
                  isSidebarOpen ? 'border-4 border-[#ffd200] bg-[#fffef0]' : 'border-4 border-transparent'
                }`}
                aria-label="Toggle sidebar menu"
                style={{
                  minWidth: '50px',
                  minHeight: '50px',
                  zIndex: 1001,
                  padding: '8px'
                }}
              >
                <i className="fa fa-bars text-white bg-[#ffd200] w-[34px] h-[34px] leading-[34px] rounded-full text-base text-center inline-block"></i>
              </button>
            )}

            <button
              className="md:hidden flex flex-col bg-none border-none cursor-pointer p-1"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className="w-6 h-0.5 bg-jet-primary my-0.5 transition-all duration-300"></span>
              <span className="w-6 h-0.5 bg-jet-primary my-0.5 transition-all duration-300"></span>
              <span className="w-6 h-0.5 bg-jet-primary my-0.5 transition-all duration-300"></span>
            </button>
          </div>
        </div>
      </div>

      {/* Floating Sidebar */}
      <FloatingSidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
    </header>
  );
};

export default Header;